import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ThemeProvider as StyledThemeProvider } from "styled-components";
import {
  createMuiTheme,
  ThemeProvider as MuiV4ThemeProvider,
} from "@material-ui/core";
import {
  createTheme,
  ThemeProvider as MuiV5ThemeProvider,
} from "@mui/material/styles";
import { isXavierThemeV2Enabled } from "../../utils/FeatureFlags";
import "./tokens.css";

const STORAGE_KEY = "xavier-theme-v2";
const XavierThemeContext = createContext({
  enabled: false,
  mode: "light",
  toggleMode: () => {},
  setMode: () => {},
});

const getSystemMode = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";

const getInitialMode = () => {
  if (typeof window === "undefined") {
    return "light";
  }

  const storedMode = window.localStorage.getItem(STORAGE_KEY);
  return storedMode === "dark" || storedMode === "light"
    ? storedMode
    : getSystemMode();
};

const xavierColors = {
  light: {
    primaryColor1: "#6e0c0e",
    primaryColor2: "#6e0c0e",
    primaryColor3: "#8a2626",
    primaryColor4: "#c8877e",
    secondaryColor2: "#efe1dd",
    alertColor: "#a3181b",
    textColor1: "#171515",
    textColor2: "#444d52",
    textColor3: "#667075",
    colorWhite: "#ffffff",
    backgroundColor1: "#f7f6f3",
    backgroundColor2: "#f0eeeb",
  },
  dark: {
    primaryColor1: "#c8877e",
    primaryColor2: "#c8877e",
    primaryColor3: "#e4c4bc",
    primaryColor4: "#6e0c0e",
    secondaryColor2: "#3a2223",
    alertColor: "#df8c83",
    textColor1: "#f5f0ee",
    textColor2: "#d4c7c4",
    textColor3: "#af9f9c",
    colorWhite: "#211719",
    backgroundColor1: "#171112",
    backgroundColor2: "#2a1e20",
  },
};

export const useXavierTheme = () => useContext(XavierThemeContext);

export default function XavierThemeProvider({ children, legacyTheme }) {
  const enabled = isXavierThemeV2Enabled();
  const [mode, setModeState] = useState(getInitialMode);

  const setMode = useCallback((nextMode) => {
    const safeMode = nextMode === "dark" ? "dark" : "light";
    window.localStorage.setItem(STORAGE_KEY, safeMode);
    setModeState(safeMode);
  }, []);

  const toggleMode = useCallback(
    () => setMode(mode === "dark" ? "light" : "dark"),
    [mode, setMode]
  );

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;

    if (!enabled) {
      root.classList.remove("xavier-theme-v2");
      body.classList.remove("xavier-theme-v2");
      root.removeAttribute("data-xavier-theme");
      body.removeAttribute("data-xavier-theme");
      return undefined;
    }

    root.classList.add("xavier-theme-v2");
    body.classList.add("xavier-theme-v2");
    root.setAttribute("data-xavier-theme", mode);
    body.setAttribute("data-xavier-theme", mode);

    return () => {
      root.classList.remove("xavier-theme-v2");
      body.classList.remove("xavier-theme-v2");
      root.removeAttribute("data-xavier-theme");
      body.removeAttribute("data-xavier-theme");
    };
  }, [enabled, mode]);

  const theme = useMemo(
    () =>
      enabled
        ? {
            ...legacyTheme,
            colors: xavierColors[mode],
            xavier: { enabled, mode },
          }
        : legacyTheme,
    [enabled, legacyTheme, mode]
  );

  const contextValue = useMemo(
    () => ({ enabled, mode, setMode, toggleMode }),
    [enabled, mode, setMode, toggleMode]
  );

  const muiThemeOptions = useMemo(
    () => ({
      palette: {
        type: mode,
        mode,
        primary: {
          main: xavierColors[mode].primaryColor1,
          contrastText:
            mode === "dark" ? "#1a0c0c" : xavierColors[mode].colorWhite,
        },
        secondary: {
          main: xavierColors[mode].secondaryColor2,
          contrastText: xavierColors[mode].textColor1,
        },
        background: {
          default: xavierColors[mode].backgroundColor1,
          paper: mode === "dark" ? "#211719" : "#fffdfc",
        },
        text: {
          primary: xavierColors[mode].textColor1,
          secondary: xavierColors[mode].textColor3,
        },
        error: { main: xavierColors[mode].alertColor },
      },
      typography: {
        fontFamily:
          '"League Spartan", "Helvetica Neue", Helvetica, Arial, sans-serif',
        h1: { fontFamily: '"Philosopher", Georgia, serif' },
        h2: { fontFamily: '"Philosopher", Georgia, serif' },
        h3: { fontFamily: '"Philosopher", Georgia, serif' },
        h4: { fontFamily: '"Philosopher", Georgia, serif' },
        h5: { fontFamily: '"Philosopher", Georgia, serif' },
        h6: { fontFamily: '"Philosopher", Georgia, serif' },
      },
      shape: { borderRadius: 6 },
      props: {
        MuiButtonBase: { disableRipple: true },
        MuiPopover: { elevation: 1 },
      },
      components: {
        MuiButtonBase: {
          defaultProps: { disableRipple: true },
        },
      },
    }),
    [mode]
  );

  const muiV4Theme = useMemo(
    () => createMuiTheme(muiThemeOptions),
    [muiThemeOptions]
  );
  const muiV5Theme = useMemo(
    () => createTheme(muiThemeOptions),
    [muiThemeOptions]
  );

  const themedChildren = enabled ? (
    <MuiV4ThemeProvider theme={muiV4Theme}>
      <MuiV5ThemeProvider theme={muiV5Theme}>{children}</MuiV5ThemeProvider>
    </MuiV4ThemeProvider>
  ) : (
    children
  );

  return (
    <XavierThemeContext.Provider value={contextValue}>
      <StyledThemeProvider theme={theme}>{themedChildren}</StyledThemeProvider>
    </XavierThemeContext.Provider>
  );
}
